import { useEffect, useRef } from "react";

type VersionPayload = {
  builtAt?: string;
  version?: string;
};

type UseAppVersionRefreshOptions = {
  checkIntervalMs?: number;
  scheduledCheckHours?: number[];
  /** Receives the hook's own reload path — use it for the toast action,
   *  so a wedged service worker is shifted before the page reloads. */
  onUpdateAvailable?: (reload: () => Promise<void>) => void;
};

const DEFAULT_CHECK_INTERVAL_MS = 2 * 60 * 1000;
const DEFAULT_SCHEDULED_CHECK_HOURS = [6, 18];

/**
 * The commit THIS bundle was built from, inlined by vite (see the `define`
 * block). Empty on a local dev build, where there is nothing to compare.
 *
 * Load-bearing. Without it this hook seeded its baseline from the first fetch
 * of version.json — the server's CURRENT version — so it could only ever
 * notice a deploy that happened while the tab was open. A tab that started out
 * running a stale bundle compared the server against itself, found no
 * mismatch, and stayed stale indefinitely. That is exactly what a wedged
 * service worker produces, and it is why months-old bundles ran undetected.
 */
const buildVersion = () => process.env.VITE_APP_VERSION || "";

/** Set once we have already tried to shift a stuck service worker this tab. */
const SW_REFRESH_ATTEMPTED_KEY = "catchment-sw-refresh-attempted";
/** How many self-heal reloads this tab has already performed. */
const RELOAD_ATTEMPTS_KEY = "catchment-version-reload-attempts";
/**
 * Reloads allowed per tab before we stop and leave the toast up.
 *
 * Two is enough for the case this can actually fix — one to let the worker
 * update, one after it has been unregistered. Past that the client is stuck on
 * something we cannot reach from here (a proxy still serving the old sw.js),
 * and reloading again would spin: seeding from the running build means a
 * permanently-stale bundle mismatches on EVERY fresh document, so without this
 * cap a backgrounded tab reloads several times a second, forever. That is a
 * regression this hook did not have before the seed changed — the old
 * seed-from-server behaviour made a fresh document find no mismatch at all.
 */
const MAX_RELOAD_ATTEMPTS = 2;

const readCounter = (key: string) => {
  try {
    return Number(sessionStorage.getItem(key)) || 0;
  } catch {
    // Storage blocked. Treat as exhausted rather than risk an unbounded loop.
    return MAX_RELOAD_ATTEMPTS;
  }
};

/**
 * Forget this tab's self-heal state. Called once the running build matches what
 * is deployed: without it, `SW_REFRESH_ATTEMPTED_KEY` persists across reloads
 * and the NEXT ordinary deploy seen by a long-lived tab would skip `update()`
 * and unregister a perfectly healthy service worker.
 */
const clearSelfHealState = () => {
  try {
    sessionStorage.removeItem(SW_REFRESH_ATTEMPTED_KEY);
    sessionStorage.removeItem(RELOAD_ATTEMPTS_KEY);
  } catch {
    // Storage blocked; nothing was written either.
  }
};

/**
 * Reloading does not help when a service worker is answering navigations from
 * a stale precache — it just serves the same bundle again. So before reloading
 * on a version mismatch, push the worker forward; and if we already tried that
 * in this tab and are STILL on the wrong build, tear the registration down so
 * the next load has to reach the network.
 */
const refreshStuckServiceWorker = async () => {
  if (!("serviceWorker" in navigator)) {
    return;
  }
  try {
    const registration = await navigator.serviceWorker.getRegistration();
    if (!registration) {
      return;
    }
    if (sessionStorage.getItem(SW_REFRESH_ATTEMPTED_KEY)) {
      await registration.unregister();
      return;
    }
    sessionStorage.setItem(SW_REFRESH_ATTEMPTED_KEY, "1");
    await registration.update();
  } catch {
    // Storage blocked, or the worker is already gone. Reload anyway.
  }
};

const getMsUntilNextScheduledCheck = (scheduledHours: number[]) => {
  const now = new Date();
  const nextCheckTimes = scheduledHours.map((hour) => {
    const scheduledTime = new Date(now);
    scheduledTime.setHours(hour, 0, 0, 0);

    if (scheduledTime <= now) {
      scheduledTime.setDate(scheduledTime.getDate() + 1);
    }

    return scheduledTime.getTime();
  });

  return Math.min(...nextCheckTimes) - now.getTime();
};

export const useAppVersionRefresh = (
  options: UseAppVersionRefreshOptions = {}
) => {
  const {
    checkIntervalMs = DEFAULT_CHECK_INTERVAL_MS,
    onUpdateAvailable,
    scheduledCheckHours = DEFAULT_SCHEDULED_CHECK_HOURS
  } = options;
  // Seeded from the running build, not from the server's first answer — see
  // buildVersion. Falls back to the old seed-on-first-fetch behaviour only on
  // builds that carry no version (local dev).
  const currentVersionRef = useRef<string | null>(buildVersion() || null);
  const pendingReloadRef = useRef(false);
  const hasNotifiedRef = useRef(false);

  useEffect(() => {
    let isMounted = true;
    let timeoutId: number | undefined;
    const normalizedScheduledHours = Array.from(new Set(scheduledCheckHours))
      .filter((hour) => Number.isInteger(hour) && hour >= 0 && hour <= 23)
      .sort((a, b) => a - b);

    const loadVersion = async () => {
      const response = await fetch(`/version.json?t=${Date.now()}`, {
        cache: "no-store",
        headers: {
          "Cache-Control": "no-cache"
        }
      });

      if (!response.ok) {
        return null;
      }

      const payload = (await response.json()) as VersionPayload;
      return payload.version ?? null;
    };

    // Every reload this hook performs goes through here, so a wedged service
    // worker gets shifted first — reloading past one just re-serves the stale
    // precache — and so the per-tab cap applies to all of them.
    const reloadForNewVersion = async (requireStillHidden = false) => {
      const attempts = readCounter(RELOAD_ATTEMPTS_KEY);
      if (attempts >= MAX_RELOAD_ATTEMPTS) {
        // Out of road: this client cannot be healed from here. Leave the toast
        // up so the user can act, and stop reloading. See MAX_RELOAD_ATTEMPTS.
        return;
      }
      await refreshStuckServiceWorker();
      // That awaited a network call, so a backgrounded tab may be in front of
      // someone by now — don't reload out from under them mid-task.
      if (
        requireStillHidden &&
        (!document.hidden || !pendingReloadRef.current)
      ) {
        return;
      }
      try {
        sessionStorage.setItem(RELOAD_ATTEMPTS_KEY, String(attempts + 1));
      } catch {
        // Storage blocked — readCounter reports exhausted, so this is the one
        // and only reload we will do.
      }
      window.location.reload();
    };

    const reloadIfHidden = () => {
      if (pendingReloadRef.current && document.hidden) {
        void reloadForNewVersion(true);
      }
    };

    const checkForUpdates = async () => {
      try {
        const nextVersion = await loadVersion();
        if (!isMounted || !nextVersion) {
          return;
        }

        if (!currentVersionRef.current) {
          currentVersionRef.current = nextVersion;
          return;
        }

        if (nextVersion === currentVersionRef.current) {
          // Running what is deployed. Drop this tab's self-heal state, or the
          // next ordinary deploy would skip `update()` and go straight to
          // unregistering a perfectly healthy service worker.
          clearSelfHealState();
          return;
        }

        pendingReloadRef.current = true;

        if (!hasNotifiedRef.current) {
          // Hand the caller the same reload path this hook uses, so the
          // toast's "Reload Now" also shifts a wedged worker first — that is
          // the button a stranded user actually presses.
          onUpdateAvailable?.(() => reloadForNewVersion());
          hasNotifiedRef.current = true;
        }

        reloadIfHidden();
      } catch {
        // Ignore transient failures and retry on next interval/visibility event.
      }
    };

    const scheduleNextCheck = () => {
      if (!isMounted || normalizedScheduledHours.length === 0) {
        return;
      }

      timeoutId = window.setTimeout(() => {
        void (async () => {
          await checkForUpdates();
          scheduleNextCheck();
        })();
      }, getMsUntilNextScheduledCheck(normalizedScheduledHours));
    };

    const onVisibilityChange = () => {
      reloadIfHidden();

      if (!document.hidden) {
        void checkForUpdates();
      }
    };

    // Bfcache restores (back/forward navigation) neither remount this
    // component nor fire visibilitychange — the tab was visible the whole
    // time — so a stale version served just before a deploy can sit frozen
    // indefinitely. Reload unconditionally here (skip the hidden-tab gate):
    // the browser is presenting this page to the user right now regardless,
    // so there's no "interrupting active work" risk to avoid.
    const onPageShow = (event: PageTransitionEvent) => {
      if (!event.persisted) {
        return;
      }
      void (async () => {
        await checkForUpdates();
        if (pendingReloadRef.current) {
          await reloadForNewVersion();
        }
      })();
    };

    void checkForUpdates();

    const intervalId =
      normalizedScheduledHours.length === 0
        ? window.setInterval(checkForUpdates, checkIntervalMs)
        : undefined;
    scheduleNextCheck();
    document.addEventListener("visibilitychange", onVisibilityChange);
    window.addEventListener("pageshow", onPageShow);

    return () => {
      isMounted = false;
      if (intervalId) {
        window.clearInterval(intervalId);
      }
      if (timeoutId) {
        window.clearTimeout(timeoutId);
      }
      document.removeEventListener("visibilitychange", onVisibilityChange);
      window.removeEventListener("pageshow", onPageShow);
    };
  }, [checkIntervalMs, onUpdateAvailable, scheduledCheckHours]);
};
